import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { ActivityDeletionBatchView } from '@internal/spassu-code-contract';
import type { SpassuCodeApi } from '../../api';
import { DeletionBatchTracker } from './DeletionBatchTracker';

/** Opaque batch correlation id, per tab; never a credential. */
const STORAGE_KEY = 'spassu-code.deletion-batch';

function storedBatchId() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function rememberBatch(view: ActivityDeletionBatchView | null) {
  try {
    if (view?.pending) sessionStorage.setItem(STORAGE_KEY, view.batchId);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Disabled storage only prevents resumption, never in-memory monitoring.
  }
}

export function useDeletionBatchTracking(
  api: Pick<SpassuCodeApi, 'getActivityDeletionBatch'>,
  onComplete: () => void,
) {
  const [deletionBatch, setDeletionBatch] =
    useState<ActivityDeletionBatchView | null>(null);
  const tracker = useRef<DeletionBatchTracker>();
  const complete = useRef(onComplete);
  useLayoutEffect(() => {
    complete.current = onComplete;
  }, [onComplete]);
  useLayoutEffect(() => {
    const current = new DeletionBatchTracker(
      id => api.getActivityDeletionBatch(id),
      view => {
        setDeletionBatch(view);
        rememberBatch(view);
      },
      () => complete.current(),
    );
    tracker.current = current;
    const stored = storedBatchId();
    if (stored) current.resume(stored);
    return () => {
      current.stop();
      tracker.current = undefined;
    };
  }, [api]);
  const trackBatch = useCallback((view: ActivityDeletionBatchView | null) => {
    tracker.current?.track(view);
  }, []);
  const refreshBatch = useCallback(
    () => tracker.current?.refresh() ?? Promise.resolve(),
    [],
  );
  return { deletionBatch, trackBatch, refreshBatch };
}
